import { useMsal } from '@azure/msal-react';
import { KFButton } from '@kf/ic2fx';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { clearCookie } from 'utilities/getToken';
import styles from './LogOutDialog.module.scss';

interface ILogOutDialogProps {
  open: boolean;
  onClose: () => void;
}

const ACCESS_TOKEN_COOKIE_KEY = '__kfone_access_token';

export type LogoutDialogProps = ILogOutDialogProps & WithTranslation;

const LogOutDialog = ({ onClose, open, t: translate }: LogoutDialogProps) => {
  const { instance } = useMsal();
  const navigate = useNavigate();

  const handleLogOut = () => {
    onClose();
    instance?.logout();
    localStorage.clear();
    sessionStorage.clear();
    clearCookie(ACCESS_TOKEN_COOKIE_KEY);
    navigate('/', { replace: true }); // to clear the url
  };

  return (
    <Dialog
      id='logout-dialog-modal'
      data-testid='logout-dialog-modal'
      className={styles.logoutDialogBg}
      classes={{ paper: styles.logoutDialog }}
      onClose={onClose}
      open={open}
    >
      <DialogTitle
        className={'kfic2-pageTitleBold'}
        data-testid='logout-dialog-modal-title'
        tabIndex={0}
        role='presentation'
        sx={{
          '&:focus': {
            width: 'fit-content',
          },
        }}
      >
        {translate('logout')}
      </DialogTitle>
      <DialogContent className={styles.body} data-testid='logout-dialog-modal-content'>
        <Typography
          className='kfic2-bodyText'
          aria-label={`${translate('areYouSureLogout')}`}
          tabIndex={0}
          sx={{
            '&:focus': {
              width: 'fit-content',
            },
          }}
        >
          {translate('areYouSureLogout')}
        </Typography>
      </DialogContent>
      <DialogActions className={styles.actions}>
        <KFButton
          id='logout-dialog-cancel-button'
          data-testid='logout-dialog-cancel-button'
          color='quaternary'
          onClick={onClose}
          aria-label={`${translate('noLogout')} ${translate(
            'button',
          )} ${translate('pressEnterToCloseDialog')}`}
          tabIndex={0}
          role='presentation'
        >
          {translate('noLogout')}
        </KFButton>
        <KFButton
          id='logout-dialog-logout-button'
          data-testid='logout-dialog-logout-button'
          color='primary'
          onClick={handleLogOut}
          aria-label={`${translate('yesLogout')} ${translate(
            'button',
          )} ${translate('pressEnterToLogout')}`}
          tabIndex={0}
          role='presentation'
        >
          {translate('yesLogout')}
        </KFButton>
      </DialogActions>
    </Dialog>
  );
};

export default withTranslation()(LogOutDialog);
